import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useProfile } from "@/hooks/useProfile";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Lightbulb, Loader2, Sparkles, CheckCircle2, XCircle, Plus } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface AIReviewSuggestions {
  title: string;
  titleSuggestion?: string;
  description: string;
  descriptionSuggestion?: string;
  suggestedDomainIds: string[];
  domainConfidence: Record<string, number>;
  overallConfidence: number;
  domainReasonings?: Array<{ id: string; reasoning: string }>;
}

export default function KnowledgeBaseReview() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile } = useProfile();
  const queryClient = useQueryClient();

  const { 
    aiSuggestions, 
    uploadedFiles, 
    sourceMethod,
    userTitle,
    userDescription,
    extractedContent,
    selectedChannelId,
    selectedChannelName
  } = location.state || {};

  const [title, setTitle] = useState(userTitle || aiSuggestions?.title || "");
  const [description, setDescription] = useState(userDescription || aiSuggestions?.description || "");
  const [selectedDomainIds, setSelectedDomainIds] = useState<string[]>(aiSuggestions?.suggestedDomainIds || []);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showCustomDomainDialog, setShowCustomDomainDialog] = useState(false);
  const [customDomainName, setCustomDomainName] = useState("");
  const [customDomainSymbol, setCustomDomainSymbol] = useState("");
  const [customDomainDescription, setCustomDomainDescription] = useState("");

  // Fetch domains filtered by channel
  const { data: domains } = useQuery({
    queryKey: ['domains', selectedChannelId],
    queryFn: async () => {
      let query = supabase
        .from('domains')
        .select('*')
        .order('name');
      
      // Filter by channel if one is selected
      if (selectedChannelId) {
        query = query.eq('channel_id', selectedChannelId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    }
  });

  // Redirect if no data
  useEffect(() => {
    if (!aiSuggestions) {
      toast({
        title: "Missing data",
        description: "Please start from the KB creation page",
        variant: "destructive"
      });
      navigate("/creators");
    }
  }, [aiSuggestions, navigate, toast]);

  const handleUseTitleSuggestion = () => {
    if (aiSuggestions?.titleSuggestion) {
      setTitle(aiSuggestions.titleSuggestion);
      toast({
        title: "Title updated",
        description: "Using Giblet's suggested title"
      });
    }
  };

  const handleUseDescriptionSuggestion = () => {
    if (aiSuggestions?.descriptionSuggestion) {
      setDescription(aiSuggestions.descriptionSuggestion);
      toast({
        title: "Description updated",
        description: "Using Giblet's suggested description"
      });
    }
  };

  const toggleDomain = (domainId: string) => {
    setSelectedDomainIds(prev =>
      prev.includes(domainId)
        ? prev.filter(id => id !== domainId)
        : [...prev, domainId]
    );
  };

  const handleCreateCustomDomain = async () => {
    if (!customDomainName.trim()) {
      toast({
        title: "Error",
        description: "Domain name is required",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create the new domain
      const { data: newDomain, error: domainError } = await supabase
        .from('domains')
        .insert({
          name: customDomainName.trim(),
          symbol: customDomainSymbol.trim() || '📁',
          description: customDomainDescription.trim() || null,
          channel_id: selectedChannelId,
        })
        .select()
        .single();

      if (domainError) throw domainError;

      // Add the new domain to selection
      setSelectedDomainIds([...selectedDomainIds, newDomain.id]);

      // Refetch domains to include the new one
      await queryClient.invalidateQueries({ queryKey: ['domains', selectedChannelId] });

      toast({
        title: "Success",
        description: `Created new domain: ${customDomainName}`,
      });

      // Reset dialog
      setShowCustomDomainDialog(false);
      setCustomDomainName('');
      setCustomDomainSymbol('');
      setCustomDomainDescription('');
    } catch (error) {
      console.error('Error creating custom domain:', error);
      toast({
        title: "Error",
        description: "Failed to create domain. It may already exist.",
        variant: "destructive",
      });
    }
  };

  const handlePublish = async () => {
    if (!title.trim()) {
      toast({
        title: "Title required",
        description: "Please provide a title for your knowledge base",
        variant: "destructive"
      });
      return;
    }

    if (selectedDomainIds.length === 0) {
      toast({
        title: "Domain required",
        description: "Please select at least one knowledge domain",
        variant: "destructive"
      });
      return;
    }

    setIsPublishing(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Create KB record
      const { data: kb, error: kbError } = await supabase
        .from('knowledge_bases')
        .insert({
          creator_id: user.id,
          title: title.trim(),
          description: description.trim() || null,
          status: 'published',
          published_at: new Date().toISOString(),
          file_url: uploadedFiles?.[0]?.url,
          file_name: uploadedFiles?.[0]?.name,
          file_type: uploadedFiles?.[0]?.type,
          file_size: uploadedFiles?.[0]?.size,
          channel_id: selectedChannelId || null,
          channel_name: selectedChannelName || null,
        })
        .select()
        .single();

      if (kbError) throw kbError;

      // Auto-populate domains using RPC
      const { error: domainsError } = await supabase.rpc('auto_populate_kb_domains', {
        kb_id: kb.id,
        domain_ids: selectedDomainIds
      });

      if (domainsError) {
        console.error('Error populating domains:', domainsError);
      }

      // Create attribution
      await supabase.from('attributions').insert({
        knowledge_base_id: kb.id,
        user_id: user.id,
        ownership_percentage: 100,
        contribution_type: 'creator'
      });

      toast({
        title: "Knowledge Base Published! 🎉",
        description: "Your knowledge base is now live and categorized"
      });

      if (profile?.username) {
        navigate(`/myspai-profile/${profile.username}`);
      } else {
        navigate('/create-myspai-profile');
      }
    } catch (error) {
      console.error('Error publishing KB:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to publish knowledge base",
        variant: "destructive"
      });
    } finally {
      setIsPublishing(false);
    }
  };

  if (!aiSuggestions) return null;

  const confidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return "text-green-600";
    if (confidence >= 0.6) return "text-yellow-600";
    return "text-orange-600";
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <Sparkles className="text-primary" />
            Review & Refine Your Knowledge Base
          </h1>
          <p className="text-muted-foreground">
            Giblet has analyzed your content and suggested metadata. Review and adjust as needed.
          </p>
        </div>

        {selectedChannelName && (
          <Card className="p-4 mb-6 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="text-green-600" size={20} />
                  <span className="font-semibold text-green-900 dark:text-green-100">
                    Channel: {selectedChannelName}
                  </span>
                </div>
                <p className="text-sm text-green-800 dark:text-green-200">
                  Only {selectedChannelName} domain categories are available for selection below
                </p>
              </div>
              <Badge variant="outline" className="bg-green-100 border-green-300 text-green-700">
                {selectedChannelName} Domains
              </Badge>
            </div>
          </Card>
        )}

        {aiSuggestions.overallConfidence > 0 && (
          <Card className="p-4 mb-6 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="text-blue-600" size={20} />
              <span className="font-semibold text-blue-900 dark:text-blue-100">
                Giblet's Confidence: {Math.round(aiSuggestions.overallConfidence * 100)}%
              </span>
            </div>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Based on content analysis, Giblet is {Math.round(aiSuggestions.overallConfidence * 100)}% confident in these categorizations.
            </p>
          </Card>
        )}

        <Card className="p-6 mb-6">
          <div className="space-y-6">
            {/* Title Input */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Title <span className="text-destructive">*</span>
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter knowledge base title"
                className="mb-2"
              />
              {aiSuggestions.titleSuggestion && title !== aiSuggestions.titleSuggestion && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUseTitleSuggestion}
                  className="w-full justify-start text-left"
                >
                  <Lightbulb className="mr-2" size={16} />
                  <span className="flex-1">
                    Giblet suggests: <span className="font-semibold">{aiSuggestions.titleSuggestion}</span>
                  </span>
                </Button>
              )}
            </div>

            {/* Description Input */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Description
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter knowledge base description"
                rows={4}
                className="mb-2"
              />
              {aiSuggestions.descriptionSuggestion && description !== aiSuggestions.descriptionSuggestion && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUseDescriptionSuggestion}
                  className="w-full justify-start text-left"
                >
                  <Lightbulb className="mr-2" size={16} />
                  <span className="flex-1 line-clamp-2">
                    Giblet suggests: <span className="font-semibold">{aiSuggestions.descriptionSuggestion}</span>
                  </span>
                </Button>
              )}
            </div>

            {/* Domain Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Knowledge Domains <span className="text-destructive">*</span>
              </label>
              
              {/* Currently Selected Domains */}
              {selectedDomainIds.length > 0 && (
                <div className="mb-4 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-3 flex items-center justify-between">
                    <span>Selected Domains ({selectedDomainIds.length})</span>
                    <span className="text-sm font-normal text-green-700">Click to remove</span>
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedDomainIds.map(domainId => {
                      const domain = domains?.find(d => d.id === domainId);
                      return domain ? (
                        <Badge 
                          key={domainId}
                          className="bg-green-600 text-white px-3 py-1.5 cursor-pointer hover:bg-green-700 flex items-center gap-2"
                          onClick={() => toggleDomain(domainId)}
                        >
                          {domain.name}
                          <XCircle className="h-3 w-3" />
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              
              {selectedDomainIds.length === 0 && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    No domains selected yet. Please select at least one domain from the available options below.
                  </p>
                </div>
              )}
              
              <p className="text-sm text-muted-foreground mb-3">
                {selectedDomainIds.length > 0 ? 'Add more domains or click selected ones to remove them.' : 'Giblet has automatically categorized your content. Click domains to add them.'}
              </p>
              <TooltipProvider>
                <div className="flex flex-wrap gap-2">
                  {domains?.map(domain => {
                    const isSelected = selectedDomainIds.includes(domain.id);
                    const confidence = aiSuggestions.domainConfidence[domain.id];
                    const reasoning = aiSuggestions.domainReasonings?.find(r => r.id === domain.id)?.reasoning;
                    const isAISuggested = aiSuggestions.suggestedDomainIds.includes(domain.id);

                    return (
                      <Tooltip key={domain.id}>
                        <TooltipTrigger asChild>
                          <Badge
                            variant={isSelected ? "default" : "outline"}
                            className="cursor-pointer transition-all hover:scale-105"
                            onClick={() => toggleDomain(domain.id)}
                          >
                            {isSelected ? (
                              <CheckCircle2 className="mr-1" size={14} />
                            ) : (
                              <XCircle className="mr-1" size={14} />
                            )}
                            {domain.name}
                            {isAISuggested && confidence && (
                              <span className={`ml-1 text-xs ${confidenceColor(confidence)}`}>
                                {Math.round(confidence * 100)}%
                              </span>
                            )}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p className="text-sm">{domain.description || domain.name}</p>
                          {reasoning && (
                            <p className="text-xs text-muted-foreground mt-1 pt-1 border-t">
                              AI Reasoning: {reasoning}
                            </p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                  
                  {/* "Other" option for creating new domains */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className="cursor-pointer transition-all hover:scale-105 border-dashed"
                        onClick={() => setShowCustomDomainDialog(true)}
                      >
                        <Plus className="mr-1" size={14} />
                        Other (Create New Domain)
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-sm">Create a new domain for this channel</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
              {selectedDomainIds.length === 0 && (
                <p className="text-sm text-destructive mt-2">Please select at least one domain</p>
              )}
            </div>
          </div>
        </Card>

        {/* Content Preview for Debugging */}
        {extractedContent && (
          <Card className="p-4 mb-6">
            <Collapsible>
              <CollapsibleTrigger className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2">
                <Sparkles size={14} />
                Show extracted content preview (for debugging)
              </CollapsibleTrigger>
              <CollapsibleContent>
                <pre className="text-xs bg-muted p-3 rounded mt-2 max-h-40 overflow-auto whitespace-pre-wrap">
                  {extractedContent.substring(0, 1000)}...
                </pre>
                <p className="text-xs text-muted-foreground mt-2">
                  Total extracted: {extractedContent.length} characters
                </p>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        )}

        {/* Custom Domain Dialog */}
        <Dialog open={showCustomDomainDialog} onOpenChange={setShowCustomDomainDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Domain for {selectedChannelName}</DialogTitle>
              <DialogDescription>
                This will create a new domain that will appear on the {selectedChannelName} channel.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="custom-domain-name">Domain Name *</Label>
                <Input
                  id="custom-domain-name"
                  value={customDomainName}
                  onChange={(e) => setCustomDomainName(e.target.value)}
                  placeholder="e.g., Workforce Development"
                />
              </div>
              
              <div>
                <Label htmlFor="custom-domain-symbol">Symbol (Emoji)</Label>
                <Input
                  id="custom-domain-symbol"
                  value={customDomainSymbol}
                  onChange={(e) => setCustomDomainSymbol(e.target.value)}
                  placeholder="e.g., 👷"
                  maxLength={2}
                />
              </div>
              
              <div>
                <Label htmlFor="custom-domain-description">Description</Label>
                <Textarea
                  id="custom-domain-description"
                  value={customDomainDescription}
                  onChange={(e) => setCustomDomainDescription(e.target.value)}
                  placeholder="Brief description of this domain"
                  rows={3}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCustomDomainDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCustomDomain} disabled={!customDomainName.trim()}>
                Create & Select Domain
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={isPublishing}
            className="flex-1"
          >
            Go Back
          </Button>
          <Button
            onClick={handlePublish}
            disabled={isPublishing || !title.trim() || selectedDomainIds.length === 0}
            className="flex-1"
          >
            {isPublishing ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={16} />
                Publishing...
              </>
            ) : (
              <>
                <Sparkles className="mr-2" size={16} />
                Publish Knowledge Base
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
